<template>
  <b-navbar
    class="PageNav justify-content-end position-fixed fixed-top"
    type="dark"
  >
    <router-link
      :to="{ name: 'Dashboard' }"
      class="evtools-logo"
    >
      <EVToolsLogo
        context="navbar"
      />
    </router-link>
    <b-navbar-nav
      id="mainNavbar"
      class="d-flex mr-3 flex-gap-1"
      align="right"
      variant="light"
    >
      <b-nav-item
        v-if="showRequestList"
        :to="{ name: 'RequestList' }"
      >
        {{ $t('navbar.requests') }}
      </b-nav-item>
      <div v-if="showRealisationProcessList">
        <b-nav-item
          v-if="!hasOnlyRealisationProcessType"
          id="processesNavBar"
          class="process-dropdown d-flex justify-content-center"
          :active="isRouteActive"
        >
          <b-dropdown
            id="processDropdown"
            ref="processDropdown"
            size="sm"
            :toggle-class="['process', { 'link active': isRouteActive }]"
            :text="selectedProcessLabel"
            right
          >
            <b-dropdown-item-button
              v-for="processType in processTypes"
              id="processDropdown"
              :key="processType.value"
              :active="activeProcessItem({ processType })"
              @click="handleLoadProcessType({ processType })"
            >
              <div><span>{{ $t(`navbar.process_types.${processType.value}`) }} <small>({{ processType.acronym }})</small></span></div>
            </b-dropdown-item-button>
          </b-dropdown>
        </b-nav-item>
        <b-nav-item
          v-else
          :to="{ name: 'ProcessList', params: { type: getType() } }"
          :active="isRouteActive"
        >
          {{ $t('navbar.processes') }}
        </b-nav-item>
      </div>

      <b-nav-item
        v-if="showLocationList"
        :to="{ name: 'LocationList' }"
      >
        {{ $t('navbar.locations') }}
      </b-nav-item>
      <b-nav-item
        v-if="canAccessReporting"
        :to="{ name: 'Reporting' }"
      >
        {{ $t('navbar.reports') }}
      </b-nav-item>
    </b-navbar-nav>

    <b-dropdown
      v-if="showRealistionProcessButton && newProcessOptions.length > 1"
      variant="light"
      size="sm"
      :text="$t('navbar.new_realisation_process')"
      right
      class="mr-3"
    >
      <b-dropdown-item-button
        v-for="option in newProcessOptions"
        :key="option.value"
        :disabled="(!hasPrivateProcessType && option.value === 'new-private')"
        @click="handleStartNewProcess(option)"
      >
        <div class="d-flex justify-content-between">
          <div>{{ option.text }}</div>
          <i class="icon-button fa-fw fal fa-plus mt-1" />
        </div>
      </b-dropdown-item-button>
    </b-dropdown>
    <b-button
      v-else-if="showRealistionProcessButton && newProcessOptions.length === 1"
      variant="light"
      size="sm"
      class="mr-3"
      @click="handleStartNewProcess(newProcessOptions[0])"
    >
      {{ $t('navbar.new_realisation_process') }} <i class="icon-button fa-fw fal fa-plus mt-1" />
    </b-button>

    <router-link
      class="d-inline-flex align-center mr-3"
      :to="{ name: 'ReleaseNotes' }"
    >
      <i class="icon fa-fw fal fa-rocket-launch" />
    </router-link>
    <router-link
      v-if="canAccessManuals"
      class="d-inline-flex align-center mr-3"
      :to="{ name: 'Manuals' }"
    >
      <i class="icon fa-fw fal fa-circle-question" />
    </router-link>

    <router-link
      v-if="canAccessConfig"
      class="d-inline-flex align-center mr-3"
      :to="{ name: 'Config' }"
    >
      <i class="icon fa-fw fal fa-gear" />
    </router-link>

    <button
      type="button"
      class="NavBar__Apps d-inline-flex align-center mr-3"
      :class="{ 'NavBar__Apps--visible': showingApps }"
      :aria-label="$t('NavBarApps.title')"
      :aria-expanded="String(showingApps)"
      aria-haspopup="true"
      @click="showApps"
    >
      <i class="icon fa-fw fal fa-grid-2" />
    </button>

    <span
      class="NavBar__Profile d-inline-flex align-center mr-3"
      :class="{ 'NavBar__Profile--visible': showingProfile }"
      @click="showProfile"
    >
      <i class="icon fa-fw fal fa-user" />
    </span>

    <NavBarApps
      :style="{ right: profileCardOffset, display: showingApps ? 'block' : 'none' }"
    />

    <ProfileCard
      :style="{ right: profileCardOffset, display: showingProfile ? 'block' : 'none' }"
    />
  </b-navbar>
</template>

<script>
import EVToolsLogo from '@/components/EVToolsLogo.vue'
import NavBarApps from '@/components/navbar/NavBarApps.vue'
import ProfileCard from '@/components/profile/ProfileCard.vue'

import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'PageNav',
  components: {
    EVToolsLogo,
    NavBarApps,
    ProfileCard
  },
  data() {
    return {
      showingProfile: false,
      showingApps: false,
      selectedProcessType: null,
      processTranslation: {
        realisatie: 'Realisatieprocessen',
        bijplaatsing: 'Bijplaatsprocessen',
        verplaatsing: 'Verplaatsprocessen',
        verwijdering: 'Verwijderprocessen',
        vervanging: 'Vervangprocessen',
        verzwaring: 'Verzwaarprocessen'
      },
      realisations: ['brussels', 'wallonia'],
      isRouteActive: false
    }
  },
  computed: {
    ...mapGetters('user', [
      'canAccessRequests',
      'canAccessRealisationProcesses',
      'canStartRealisationProcess',
      'canAccessAssetManagement',
      'canAccessManuals',
      'canAccessConfig',
      'canAccessReporting',
      'canAccessPrivateProcesses'
    ]),
    ...mapGetters('overlay', ['isOverlayVisible']),
    ...mapGetters('tenant', ['getActiveProcessTypes', 'getCategoryOptions', 'getTenant']),
    ...mapGetters('processes', ['getCurrentProcessType']),

    customRealisations () {
      return this.processTypes.filter(type => this.realisations.includes(type.value))
    },

    newProcessOptions() {
      if (this.getTenant?.slug === 'rvb') {
        return [
          { text: 'Privaat terrein', value: 'new-private' }
        ]
      }

      const routes = [
        ...( this.hasRealisationProcess
          ? [{ text: this.$t('navbar.public_domain'), value: 'NewRealisation' }]
          : []
        ),
        { text: this.$t('navbar.private_domain'), value: 'new-private' }
      ]

      if (this.customRealisations.length) {
        this.customRealisations.map(type => routes.push(
          {
            text: this.$t(`processes.sidebar.process_types.${type.value}`),
            value: `new-${type.value}`
          }
        ))
      }
      return routes
    },

    showRequestList() {
      return this.canAccessRequests
    },
    showRealisationProcessList() {
      return this.canAccessRealisationProcesses
    },
    showRealistionProcessButton() {
      return this.canStartRealisationProcess
    },
    showLocationList() {
      return this.canAccessAssetManagement
    },

    selectedProcessLabel () {
      return this.$t(`navbar.process_types.${this.selectedProcessType?.value}`)
    },
    processTypes () {
      // For the time being, filter out the 'capacity-increase' //
      return this.getActiveProcessTypes.filter(type => {
        if (type.value === 'private' && !this.canAccessPrivateProcesses) {
          return false
        }
        return !['CIP'].includes(type.acronym)
      })
    },
    routeType () {
      // If newProcess route but with wrong name, return early (until we find the cause) //
      if (this.$route.path.includes('new-') && !this.$route.name.startsWith('new')) {
        return this.getActiveProcessTypes.find(type => type.value === this.$route.path.split('new-').pop())?.routeType
      }

      const currentProcessType = (this.$route.name.startsWith('new') && this.$route.name.split('-').pop()) || this.getCurrentProcessType
      const route = this.getActiveProcessTypes.find(type => type.value === currentProcessType) ||
                    this.getActiveProcessTypes.at(0)

      return route.routeType
    },
    hasRealisationProcess () {
      return this.getActiveProcessTypes.some(process => process.value === 'realisation')
    },
    hasPrivateProcessType () {
      return this.getActiveProcessTypes.some(process => process.value === 'private')
    },
    hasOnlyRealisationProcessType () {
      return this.getActiveProcessTypes.every(process => process.value === 'realisation') || this.getActiveProcessTypes.length === 1
    },

    /**
     * We need to avoid the profile card covering up the scrollbar of the main page content
     */
    profileCardOffset() {
      return '1.25em' // `${getScrollbarWidth()}px`
    },

    /**
     * One overlay serves every navbar dropdown. Deriving its state instead of
     * watching each flag keeps switching between dropdowns from toggling it twice
     */
    isDropdownVisible() {
      return this.showingProfile || this.showingApps
    }
  },
  watch: {
    isOverlayVisible() {
      if (! this.isOverlayVisible) {
        this.showingProfile = false
        this.showingApps = false
      }
    },
    isDropdownVisible() {
      if (this.isDropdownVisible) {
        this.showOverlay()
        this.unfreezeOverlay()
      } else {
        this.hideOverlay()
      }
    },
    '$route': {
      immediate: true,
      handler (route) {
        if (!route.name) return;
        // Remove selected record from store if current route has no param uuid //
        if (!route.params.uuid) {
          this.$store.dispatch('realisations/setSelectedRecord', { record: null })
          this.$store.dispatch('processes/setSelectedProcess', { process: null })
          this.$store.dispatch('chargingpoints/setActiveMunicipality', { code: '' })
          this.$store.dispatch('chargingpoints/resetRecords')
        }

        // For /process/new-wallonia, route.params.type is 'process' (the literal URL path segment from /:type/:uuid), //
        // which is truthy — No active process type has routeType === 'process', so selectedProcessType becomes undefined. //
        // The fix: validate that route.params.type actually matches a known process type before using it. //
        // UPDATE: Fix is not complete. Computed prop. routeType still generated NULL for a routeType //
        // so the early return is introduced (temp) until the real cause is found //
        const validRouteType = this.getActiveProcessTypes.some(t => t.routeType === route.params.type)
          ? route.params.type
          : this.routeType

        this.selectedProcessType = this.getActiveProcessTypes.find(type => {
          return type.routeType === validRouteType
        })

        if (this.selectedProcessType && (this.selectedProcessType.value !== this.getCurrentProcessType)) {
          this.$store.dispatch('processes/setCurrentProcessType', { processType: this.selectedProcessType?.value })
        }

        this.isRouteActive = /^ProcessList|^Step|^RealisationStep|^new/i.test(this.$route.name) ||
                            (this.getCurrentProcessType &&
                            (this.$route.name.startsWith(this.getCurrentProcessType) || this.$route.params.type === this.routeType))
      }
    }
  },
  methods: {
    ...mapMutations('overlay', [
      'hideOverlay',
      'showOverlay',
      'unfreezeOverlay',
    ]),
    activeProcessItem ({ processType }) {
      //console.log('PAGE NAV ACTIVE', this.selectedProcessType.value, processType.value)
      return processType.value === this.selectedProcessType?.value
    },
    handleStartNewProcess(option) {
      let route = {
        name: option.value
      }

      this.$router.push(route)
      // The option value after "new" => processType //
      if (option.value.startsWith('new-')) {
        const processType = option.value.split('-')?.pop()
        this.selectedProcessType = this.getActiveProcessTypes.find(type => type.value === processType)
        this.$store.dispatch('processes/setCurrentProcessType', { processType })
      }

    },
    handleLoadProcessType ({ processType }) {
      this.selectedProcessType = processType

      if (this.$route.name !== 'ProcessList') {
        this.$router.push({ name: 'ProcessList', params: { type: processType.routeType } })
      } else {
        this.$router.replace({ params: { type: processType.routeType } })
      }
    },
    logout() {
      this.$auth.logout({
        returnTo: window.location.origin,
      })
    },
    showProfile() {
      this.showingApps = false
      this.showingProfile = ! this.showingProfile
    },
    showApps() {
      this.showingProfile = false
      this.showingApps = ! this.showingApps
    },
    getType() {
      const activeProcessType = this.processTypes?.at(0)?.routeType
      return process.env.VUE_APP_TENANT === 'rvb'? 'privaat': (activeProcessType || 'realisatie')
    }
  },
}
</script>

<style lang="scss">
.PageNav {
  margin: 0 auto;
  min-height: $menu-height;

  // EV Tools primary
  background: $brand-color !important;
  padding: 0 !important;

  .icon {
    font-size: 1.7rem;
    color: white;
      &:hover{
        color: rgb(152, 147, 147);
      }
  }
  .badge {
    top: -10px;
    position: relative;
    right: 3px;
  }

  &__description {
    height: 1em;
    color: white;

    @media (max-width: 1200px) {
      span {
        display: none;
      }
    }

    @media (max-width: 920px) {
      display: none;
    }
  }

  .NavBar__Apps {
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
  }

  .ProfileCard, .NavBarApps, .NavBarMenu {
    position: absolute;
    top: calc(80px - 0.75rem);
    box-sizing: 0px 1px 3px 0px #dfe2e5;
  }

  .process-dropdown {
    .process {
      background: none !important;
      border: none !important;
      box-shadow: none !important;
      font-size: 16px;
      padding-top: 0;
      color: rgba(255, 255, 255, 0.5);
    }

    .dropdown-item {
      &.active {
        background: none;
        color: inherit;
        border: 1px solid $brand-color;
      }
    }
  }

  .evtools-logo {
    margin-right: auto;
  }

  .navbar-nav {
    .nav-link {
      padding: 0 !important;
    }

    .dropdown-toggle {
      padding: 0;
      .btn-group-vertical {
        vertical-align: unset;
      }
    }
  }
}
</style>
